<script setup lang="ts">
  import { computed } from 'vue';
  import { IconImage } from '@/shared/ui';
  import { TRAINING_CONTENT_MAX_UPLOAD_SIZE_LABEL } from '@/entities/Training';
  import type { TrainingContentUploadStatus } from '../model/useCreateTrainingContent';

  import UploadIcon from '@/shared/assets/icons/UploadIcon.svg';
  import FileTextIcon from '@/shared/assets/icons/FileTextIcon.svg';
  import UploadSuccessIcon from '@/shared/assets/icons/UploadSuccessIcon.svg';
  import GrayTrashIcon from '@/shared/assets/icons/GrayTrashIcon.svg';
  import LinkIcon from '@/shared/assets/icons/LinkIcon.svg';

  const props = defineProps<{
    file: File | null;
    fileError: string;
    url: string;
    urlError: string;
    uploadProgress: number;
    uploadStatus: TrainingContentUploadStatus;
    isUploadDisabled: boolean;
    isUrlDisabled: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'select-file', file: File | null): void;
    (e: 'remove-file'): void;
    (e: 'update:url', value: string): void;
  }>();

  const isUploading = computed(() => props.uploadStatus === 'uploading');

  const handleFileChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    emit('select-file', input.files?.[0] ?? null);
    input.value = '';
  };
</script>

<template>
  <div class="flex flex-col gap-6 border-b border-border-color px-6 py-8">
    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <span class="text-sm font-semibold text-gray-07">Upload:</span>
        <label
          data-testid="training-content-upload-button"
          class="flex h-10.5 items-center gap-3 rounded-lg border border-[var(--theme-secondary-ink)] px-4 py-3 font-medium text-[var(--theme-secondary-ink)] md:text-base text-xs"
          :class="isUploadDisabled ? 'pointer-events-none opacity-50' : 'cursor-pointer'"
          for="training-content-file-input"
        >
          <IconImage
            :src="UploadIcon"
            alt="Upload"
          />
          <span>Upload</span>
          <input
            id="training-content-file-input"
            data-testid="training-content-file-input"
            type="file"
            class="hidden"
            :disabled="isUploadDisabled"
            @change="handleFileChange"
          >
        </label>
      </div>

      <p
        v-if="!file"
        class="text-xs text-gray-03"
      >
        Tamanho máximo: {{ TRAINING_CONTENT_MAX_UPLOAD_SIZE_LABEL }}
      </p>

      <p
        v-if="fileError"
        data-testid="training-content-file-error"
        class="text-sm text-red-16"
      >
        {{ fileError }}
      </p>

      <div
        v-if="file"
        data-testid="training-content-file-row"
        class="flex items-center gap-6"
      >
        <IconImage
          :src="FileTextIcon"
          alt="Arquivo"
          class="h-6 w-6 shrink-0"
        />
        <span class="w-2/5 truncate text-sm text-gray-07">{{ file.name }}</span>
        <div class="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-02">
          <div
            data-testid="training-content-progress-bar"
            class="absolute top-0 left-0 h-full rounded-full transition-all duration-300"
            :class="uploadProgress === 100 ? 'bg-green-19' : 'bg-[var(--theme-secondary)]'"
            :style="{ width: `${uploadProgress}%` }"
          />
        </div>
        <span
          data-testid="training-content-progress-label"
          class="min-w-[44px] text-right text-sm text-gray-07"
        >
          {{ uploadProgress }}%
        </span>
        <IconImage
          v-if="uploadStatus === 'completed'"
          data-testid="training-content-upload-success"
          :src="UploadSuccessIcon"
          alt="Upload concluído"
          class="h-5 w-5 shrink-0"
        />
        <button
          v-if="!isUploading"
          type="button"
          data-testid="training-content-remove-file"
          aria-label="Remover arquivo"
          @click="emit('remove-file')"
        >
          <IconImage
            :src="GrayTrashIcon"
            alt="Remover arquivo"
            class="h-5 w-5 cursor-pointer"
          />
        </button>
      </div>
    </div>

    <div class="flex flex-col gap-1.5">
      <label
        for="training-content-url-input"
        class="text-sm font-semibold text-gray-07"
      >URL:</label>
      <div class="relative">
        <IconImage
          :src="LinkIcon"
          alt="URL"
          class="absolute top-1/2 left-3 -translate-y-1/2"
        />
        <input
          id="training-content-url-input"
          data-testid="training-content-url-input"
          :value="url"
          :disabled="isUrlDisabled"
          type="text"
          placeholder="Cole aqui a URL que os usuários devem acessar"
          class="w-full rounded-lg border py-3 pr-4 pl-10 text-sm text-gray-07 focus:outline-none disabled:bg-gray-02/20 disabled:text-gray-03"
          :class="urlError ? 'border-red-16' : 'border-border-color'"
          @input="emit('update:url', ($event.target as HTMLInputElement).value)"
        >
      </div>
      <p
        v-if="urlError"
        data-testid="training-content-url-error"
        class="text-sm text-red-16"
      >
        {{ urlError }}
      </p>
    </div>
  </div>
</template>
